import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  createContext,
  useContext,
} from 'react';

import 'firebase/compat/auth';
import {getAuth} from 'firebase/auth';
import {getApp} from 'firebase/app';
import {getUserDocumentRef, getUserRoleRef} from '../firebase/models';
import {useAuthState} from 'react-firebase-hooks/auth';
import {
  useDocumentData,
  useDocumentDataOnce,
} from 'react-firebase-hooks/firestore';
import {useCourseId} from '../hooks/router/useUrlParams';
import {
  doc,
  getDoc,
  getFirestore,
  setDoc,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  updateDoc,
  DocumentData,
} from 'firebase/firestore';

import type {
  DocumentReference,
  FirestoreError,
  SetOptions,
  WriteBatch,
} from 'firebase/firestore';
import type {User as FirestoreUser} from '../firebase/models';
import type {User as AuthUser} from 'firebase/auth';
import {firebaseStringToRole, isMinimumRole} from 'contexts/profileUtil';
import {useEngagement} from 'engagement/EngagementHook';
import {Role, RoleFirebaseString} from 'types/role';
import type {SectionDocument} from 'course/section/models/sectionDocument';
import type {CourseId} from 'types/common';
import {getAgeFromDateOfBirth} from 'course/application/firebase/models/formData';
import {getAnnouncedRole} from 'course/application/general/utils/decisionAnnouncement';
import type {SectionId} from 'course/section/utils';
import {
  getUserAttendanceDocumentRef,
  type UserAttendance,
} from '../firebase/models/userAttendance';
import {
  applyStaffViewAs,
  getActiveStaffViewAs,
  mergeInMemoryOnboarding,
  type StaffViewAs,
  type StaffViewAsKind,
  type StaffViewAsSeat,
} from 'course/staffViewAs/staffViewAs';
import {
  StaffViewAsContext,
  type StaffViewAsContextValue,
} from 'course/staffViewAs/StaffViewAsContext';

/**
 * CourseUser
 * A materialized view of the logged in User in the current course. Joins
 * together properties from the Firebase Auth object with the Firestore User
 * document, and precomputes some authentication values.
 */

export type CourseUser = {
  id: string;
  /* Contains the highest role the User has in the current course */
  courseRole: Role;
  /* Contains the sections the User is related to in the current course.
   * TODO: should this be references to the sections or the full section? */
  courseSections: Array<DocumentReference<SectionDocument>>;

  /* the actual dictionary on the user document */
  sections: Record<CourseId, DocumentReference<SectionDocument>[]>;

  // some people are not 18 at the start of the course!
  isMinor: boolean;

  // portfolio projects
  portfolio: Array<string>;
  okToEmail: boolean;
  okToSendNotifications: boolean;
  /**
   * Per-user opt-out for section-reminder emails (T-24h and T-1h
   * fired by the `@cip/functions` reminder cron). Defaults to `true`
   * when absent on the user doc. Outranked downstream by the global
   * `okToEmail` flag — a user with `okToEmail === false` is filtered
   * regardless of this setting.
   *
   * Coverage caveat: the reminder cron currently iterates only
   * `['cip6']`, so this field is effective only for that course
   * today. Tracked under umbrella
   * [#2587](https://github.com/CodeinPlace/cip/issues/2587) and child
   * [#2588](https://github.com/CodeinPlace/cip/issues/2588) — once the
   * cron iterates active courses this field will gate reminders for
   * all of them.
   */
  okToSendSectionReminders: boolean;
  sentFirstEmail: boolean;

  // connections
  aboutMe: string;
  goals: string;
  wantToConnect: boolean;

  // final project groups
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  finalProjectCommunities: any;

  // i18n settings
  preferredLanguage: string;
} & Omit<FirestoreUser, 'roles' | 'sections'>;

export type SetCourseUserFunction = (
  fields: Partial<FirestoreUser>,
  options?: Partial<SetOptions>,
  batch?: WriteBatch,
) => Promise<void> | undefined;

const USER_DEFAULTS: Omit<CourseUser, 'id'> = {
  photoURL: '',
  firstName: '',
  lastName: '',
  displayName: '',
  city: '',
  country: '',
  isMinor: false,
  email: '',
  shareChatGPTConvos: false,
  shareCode: false,
  sharePeerLearn: false,
  courseRole: Role.UNREGISTERED,
  courseSections: [],
  confirmedSectionLeader: {},
  confirmedReviewer: {},
  confirmedSectionOptOut: {},
  confirmedMentor: {},
  confirmedStudent: {},
  smallGroups: {},
  sections: {},
  portfolio: [],
  okToEmail: true,
  okToSendNotifications: true,
  okToSendSectionReminders: true,
  sentFirstEmail: false,
  aboutMe: '',
  goals: '',
  wantToConnect: true,
  finalProjectCommunities: {},
  preferredLanguage: 'en',
};

const defaultData: ProfileContextData = {
  userData: undefined,
  setUserData: undefined,
  loading: true,
  error: undefined,
  sectionData: undefined,
  sectionDataIsLoading: true,
  setSectionData: undefined,
  setCachedUserData: undefined,
  userProtectedData: undefined,
  userAge: undefined,
  userAttendance: null,
  privileges: [],
};

export type ProfileContextData = {
  /**
   * Materialized view of the logged-in user of the current class. "Best-effort"
   * at showing up-to-date status. It doesn't subscribe to notifications from
   * Firestore, meaning the data may be stale if the user is operating in another
   * instance of the site, but reflects any mutations from the current session
   * via an internal cache.
   */
  userData: CourseUser | undefined;

  /**
   * The user has a collection of sub docs (possibly with different write permissions)
   * This includes things like uiTraining, protected etc. Keys are doc-ids, values
   * are the docs
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  userProtectedData: any;

  /**
   * List of privilege keys the user has access to
   */
  privileges: string[];

  /**
   * Update properties of the user on the database. Also updates the internal cache
   * if the request is successful.
   *
   * NOTE: In the long-term, we should not be managing this cache ourselves; we should
   * use something like swr-firestore (https://github.com/nandorojo/swr-firestore) to
   * manage our queries and caches. Deferring until later to avoid adding another
   * query pattern to the codebase.
   */
  setUserData: SetCourseUserFunction;
  /**
   * The current status of the initial request to load the user.
   */
  loading: boolean;

  /**
   * If the initial request to load the us
   */
  error: FirestoreError | undefined;

  /**
   * If the section data has loaded
   */
  sectionDataIsLoading: boolean;

  /**
   * Maps the user's section ids to the corresponding section documents
   */
  sectionData: Record<SectionId, SectionDocument> | undefined;

  /**
   * In case we need to update the sectionData
   */
  setSectionData: React.Dispatch<
    React.SetStateAction<Record<SectionId, SectionDocument> | undefined>
  >;

  setCachedUserData: React.Dispatch<
    React.SetStateAction<CourseUser | undefined>
  >;

  /** integer number of years old. Based on userProtectedData */
  userAge: number;

  /**
   * The attendance data for the user.
   */
  userAttendance: UserAttendance | null;
};
export const ProfileContext = createContext<ProfileContextData>(defaultData);

/**
 * Wait before each automatic retry of a failed role read. The length is the
 * retry cap: once every entry is used the failure is exposed as `error`.
 */
export const ROLE_READ_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000];

type ProfileProviderProps = {
  children?: React.ReactNode;
};

/**
 * Identifies the course a mounted {@link ProfileProvider} serves. A provider
 * nested under one for the same course renders its children straight through
 * instead of subscribing to the same user documents a second time.
 */
const ProfileProviderScopeContext = createContext<{courseId: CourseId} | null>(
  null,
);

/**
 * Provides {@link ProfileContext} for the current course. Follows Firebase
 * auth state, so it may mount before sign-in resolves: `loading` stays true
 * until auth settles and, for a signed-in user, until the user documents
 * arrive. Signed-out visitors read no documents and see `userData` undefined.
 */
export const ProfileProvider = ({children}: ProfileProviderProps) => {
  const courseId = useCourseId();
  const parentScope = useContext(ProfileProviderScopeContext);
  if (parentScope !== null && parentScope.courseId === courseId) {
    return <>{children}</>;
  }
  return (
    <ProfileProviderImpl courseId={courseId}>{children}</ProfileProviderImpl>
  );
};

const ProfileProviderImpl = ({
  courseId,
  children,
}: ProfileProviderProps & {courseId: CourseId}) => {
  const [authUser, authIsLoading] = useAuthState(getAuth());
  const userId = authUser?.uid ?? null;

  console.log('ProfileProvider: userId', userId);

  const db = getFirestore(getApp());

  // Null refs while signed out, or while auth is still restoring, keep the
  // hooks idle instead of reading `users/null/...` and failing on rules.
  const userDataRef = userId ? getUserDocumentRef(userId) : null;
  const userRoleRef = userId ? getUserRoleRef(userId, courseId) : null;
  const userPrivilegesRef = userId
    ? doc(db, `users/${userId}/roles/privileges`)
    : null;
  const userProtectedRef = userId
    ? doc(db, `users/${userId}/docs/protected`)
    : null;
  const userAttendanceRef = userId
    ? getUserAttendanceDocumentRef(userId, courseId)
    : null;
  const courseDocRef =
    userId && courseId ? doc(db, `course/${courseId}`) : null;

  // this is a collection of docs associated with the user. They should all be readable
  // by the user

  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const [userData, setCachedUserData] = useState<CourseUser | undefined>(
    undefined,
  );
  const [userRole, userRoleIsLoading, userRoleError, , reloadUserRole] =
    useDocumentDataOnce(userRoleRef);
  const [roleReadRetryCount, setRoleReadRetryCount] = useState(0);
  const isRoleReadInFlightRef = useRef(false);
  const isRoleReadRetrying =
    Boolean(userRoleError) &&
    roleReadRetryCount < ROLE_READ_RETRY_DELAYS_MS.length;
  const [dbUser, dbUserIsLoading, dbUserError] = useDocumentData(userDataRef);
  const [userPrivileges, userPrivilegesIsLoading, userPrivilegesError] =
    useDocumentData(userPrivilegesRef);
  const [sectionData, setSectionData] = useState<
    Record<SectionId, SectionDocument>
  >({});
  const [sectionDataIsLoading, setSectionDataIsLoading] =
    useState<boolean>(true);
  const [userProtectedData, userProtectedLoading] =
    useDocumentData(userProtectedRef);
  const [userAttendance, userAttendanceIsLoading, userAttendanceError] =
    useDocumentData(userAttendanceRef);
  // Held here so it lasts across every page of the course until a reload.
  const [storedViewAs, setStoredViewAs] = useState<StaffViewAs | null>(null);
  // A read error leaves the course undefined, which withholds no role.
  const [courseDoc, courseDocIsLoading] = useDocumentData(courseDocRef);
  const studentAppDecisionsLive = courseDoc?.studentAppDecisionsLive;
  const slAppDecisionsLive = courseDoc?.slAppDecisionsLive;

  // Which (user, course) the cached `userData` was derived for. `loading`
  // stays true until this matches the current pair, which covers the two gaps
  // a plain flag misses: the derivation lags the documents by one effect
  // pass, and in the render where the uid or course changes the document
  // hooks still report the previous, settled state (they restart in their
  // own effects). Without this, that render briefly exposed a default profile
  // as loaded, and `useEngagement` acted on it.
  const [derivedFor, setDerivedFor] = useState<{
    userId: string | null;
    courseId: CourseId;
  } | null>(null);
  // The course document gates the role, so the role must not be derived
  // before it arrives or an unannounced admission would show for a render.
  const documentsLoading =
    authIsLoading ||
    dbUserIsLoading ||
    userRoleIsLoading ||
    userProtectedLoading ||
    courseDocIsLoading ||
    // Reporting loaded with no userData reads as an unregistered user to
    // every role gate, so a failing role read holds while it retries.
    isRoleReadRetrying;
  const userDataIsLoading =
    documentsLoading ||
    derivedFor === null ||
    derivedFor.userId !== userId ||
    derivedFor.courseId !== courseId;

  useEngagement(userDataIsLoading, userData);

  // The role is read once with no listener, so a read that fails while the
  // client is offline never recovers unless it is asked for again.
  useEffect(() => {
    if (!isRoleReadRetrying) {
      return;
    }
    // Counted on completion and never overlapped, so a late success cannot
    // land after the cap has already been reported.
    const retry = async () => {
      if (isRoleReadInFlightRef.current) {
        return;
      }
      isRoleReadInFlightRef.current = true;
      await reloadUserRole();
      isRoleReadInFlightRef.current = false;
      setRoleReadRetryCount(count => count + 1);
    };
    const retryWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        void retry();
      }
    };
    const timer = setTimeout(
      retry,
      ROLE_READ_RETRY_DELAYS_MS[roleReadRetryCount],
    );
    window.addEventListener('online', retry);
    document.addEventListener('visibilitychange', retryWhenVisible);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('online', retry);
      document.removeEventListener('visibilitychange', retryWhenVisible);
    };
  }, [isRoleReadRetrying, roleReadRetryCount, reloadUserRole]);

  useEffect(() => {
    if (documentsLoading) {
      // The documents restarted, so whatever was derived no longer describes
      // them; `loading` must hold until the next derivation lands.
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      setDerivedFor(null);
      return;
    }
    if (!authUser || dbUserError || userRoleError) {
      // Signed out, or the user documents could not be read.
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      setCachedUserData(undefined);
    } else {
      const derived = createUserData(courseId, dbUser, authUser, userRole);
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      setCachedUserData({
        ...derived,
        courseRole:
          getAnnouncedRole({
            role: derived.courseRole,
            courseData: {studentAppDecisionsLive, slAppDecisionsLive},
          }) ?? Role.UNREGISTERED,
      });
    }
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    setDerivedFor({userId, courseId});
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [
    documentsLoading,
    authUser,
    courseId,
    dbUser,
    userId,
    userRole,
    studentAppDecisionsLive,
    slAppDecisionsLive,
  ]);

  useEffect(() => {
    if (!userData) {
      return;
    }
    // The provider outlives a course switch, so a new set of course sections
    // must read as loading again until every one of them is in.
    if (!allSectionsLoaded()) {
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      setSectionDataIsLoading(true);
    }
    for (const section of userData.courseSections) {
      loadSingleSection(section);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [userData]);

  useEffect(() => {
    if (allSectionsLoaded()) {
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      setSectionDataIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [sectionData]);

  const setUserData = useCallback(
    async (
      fields: Partial<Omit<FirestoreUser, 'roles' | 'sections'>>,
      options: Partial<SetOptions> = {merge: true},
    ) => {
      if (!userDataRef) {
        return;
      }
      await setDoc(userDataRef, fields, options);
      setCachedUserData({...userData, ...fields});
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    [userDataRef],
  );

  const loadSingleSection = async (
    sectionRef:
      | DocumentReference<SectionDocument>
      | DocumentReference<DocumentData>,
  ) => {
    if (sectionRef.path) {
      sectionRef = doc(db, sectionRef.path);
    }
    const section = await getDoc(sectionRef);
    if (section.exists()) {
      const sectionData = section.data();
      setSectionData(prev => {
        return {
          ...prev,
          [section.id]: sectionData,
        };
      });
    }
  };

  const allSectionsLoaded = () => {
    if (!userData?.courseSections) return true;
    if (!sectionData) return false;

    // for each section that the user is in...
    for (const section of userData?.courseSections) {
      const sectionPath = section.path;
      // get the last part of the path
      const sectionId = sectionPath.split('/').pop();
      // check if its been loaded
      if (!sectionData[sectionId]) return false;
    }
    return true;
  };

  const isAnythingLoading =
    userDataIsLoading || userAttendanceIsLoading || userPrivilegesIsLoading;
  const error =
    dbUserError ||
    (isRoleReadRetrying ? undefined : userRoleError) ||
    userAttendanceError ||
    userPrivilegesError;

  // Get list of privilege keys from the privileges document
  const privileges = userPrivileges ? Object.keys(userPrivileges) : [];

  const scope = useMemo(() => ({courseId}), [courseId]);

  const realCourseRole = userData?.courseRole ?? Role.UNREGISTERED;
  const viewAs = getActiveStaffViewAs({
    viewAs: storedViewAs,
    courseId,
    realCourseRole,
  });
  const viewAsKind = viewAs?.kind ?? null;
  // Keyed on the kind so editing the made-up onboarding doc does not hand
  // every consumer a new profile.
  const visibleUserData = useMemo(
    () =>
      viewAsKind !== null && userData
        ? applyStaffViewAs({userData, courseId, kind: viewAsKind})
        : userData,
    [userData, courseId, viewAsKind],
  );
  const staffViewAsValue = useMemo<StaffViewAsContextValue>(
    () => ({
      viewAs,
      isStaff: isMinimumRole(realCourseRole, Role.TA),
      setViewAsKind: (
        kind: StaffViewAsKind | null,
        onboardingData: Record<string, unknown> = {},
        seat?: StaffViewAsSeat,
      ) =>
        setStoredViewAs(previous =>
          kind === null
            ? null
            : {
                courseId,
                kind,
                onboardingData,
                seat,
                sectionWeek: previous?.sectionWeek,
              },
        ),
      setViewAsSectionWeek: (sectionWeek: number | null) =>
        setStoredViewAs(previous =>
          previous === null
            ? null
            : {...previous, sectionWeek: sectionWeek ?? undefined},
        ),
      editViewAsOnboarding: (fields: Record<string, unknown>) =>
        setStoredViewAs(previous =>
          previous === null
            ? null
            : {
                ...previous,
                onboardingData: mergeInMemoryOnboarding({
                  onboardingData: previous.onboardingData,
                  fields,
                }),
              },
        ),
    }),
    [viewAs, realCourseRole, courseId],
  );

  return (
    <ProfileProviderScopeContext.Provider value={scope}>
      <StaffViewAsContext.Provider value={staffViewAsValue}>
        <ProfileContext.Provider
          value={{
            userData: visibleUserData,
            userProtectedData,
            setUserData,
            setCachedUserData,
            loading: isAnythingLoading,
            error,
            sectionData: viewAs ? NO_SECTION_DATA : sectionData,
            sectionDataIsLoading,
            setSectionData,
            userAge: calculateUserAge(userProtectedData),
            userAttendance,
            privileges: viewAs ? NO_PRIVILEGES : privileges,
          }}
        >
          {children}
        </ProfileContext.Provider>
      </StaffViewAsContext.Provider>
    </ProfileProviderScopeContext.Provider>
  );
};

const NO_SECTION_DATA: Record<SectionId, SectionDocument> = {};
const NO_PRIVILEGES: string[] = [];

/**
 * Placeholder display name assigned to a user who has no real one yet (see
 * where `computedUserData.displayName` is defaulted). Treated as "no display
 * name" by completeness checks and by the About form when deciding whether to
 * mirror the applicant's chosen name onto their profile.
 */
export const ANONYMOUS_DISPLAY_NAME = 'Anon';

export const isProfileComplete = (
  profileContext: ProfileContextData,
): boolean => {
  const userData = profileContext.userData;
  const userProtectedData = profileContext.userProtectedData;

  const hasDisplayName =
    userData.displayName &&
    userData.displayName.length > 0 &&
    userData.displayName !== ANONYMOUS_DISPLAY_NAME;
  const hasCountry = userData.country && userData.country.length > 0;
  const hasDob = userProtectedData?.dateOfBirth !== undefined;

  const hasFields = hasDisplayName && hasCountry && hasDob;
  if (!hasFields) {
    return false;
  }

  const requiredDobFields = ['day', 'month', 'year'];
  const dobFields = userProtectedData.dateOfBirth;
  const hasDobFields = requiredDobFields.every(
    field => dobFields[field] !== undefined,
  );

  return hasDobFields;
};

/**
 * Returns the current user's role in the current course.
 */
export const useUserRole = (): {role: Role; isLoading: boolean} => {
  const {userData, loading} = useContext(ProfileContext);
  return {role: userData?.courseRole ?? Role.UNREGISTERED, isLoading: loading};
};

function createUserData(
  courseId: string,
  dbUser: FirestoreUser,
  authUser: AuthUser,
  userRole: RoleFirebaseString,
): CourseUser {
  // computerUserData incrementally collects fields we compute from the underlying
  // db and auth user objects
  const computedUserData: Partial<CourseUser> = {};

  // Objects on the authUser - such as the display name, email, and
  // photo URL - are prioritized over the global defaults in USER_DEFAULTS
  // but under values that have been set in the Firestore document
  const authDisplayName = authUser?.displayName;
  if (authDisplayName) {
    const parts = authDisplayName.split(' ');
    if (parts.length >= 2) {
      const first = parts[0];
      const last = parts[parts.length - 1];
      computedUserData.displayName = authDisplayName;
      computedUserData.firstName = first;
      computedUserData.lastName = last;
    }
  }

  // Precompute authorization values for ease of client use
  const {sections, ...dbUserRest} = dbUser || {};
  // default is unregistered
  // eslint-disable-next-line prefer-const -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  let courseRole = firebaseStringToRole(userRole);

  if (dbUser?.photoURL) {
    computedUserData.photoURL = dbUser.photoURL;
  }
  if (authUser?.email) {
    computedUserData.email = authUser.email;
  }

  computedUserData.courseRole = courseRole;
  computedUserData.courseSections = sections?.[courseId] || [];
  computedUserData.displayName = dbUser?.displayName || ANONYMOUS_DISPLAY_NAME;
  computedUserData.sections = dbUser?.sections || {};
  computedUserData.portfolio = dbUser?.portfolio || [];
  computedUserData.okToEmail = dbUser?.hasOwnProperty('okToEmail')
    ? dbUser.okToEmail
    : true;
  computedUserData.okToSendNotifications = dbUser?.hasOwnProperty(
    'okToSendNotifications',
  )
    ? dbUser.okToSendNotifications
    : true;
  computedUserData.okToSendSectionReminders = dbUser?.hasOwnProperty(
    'okToSendSectionReminders',
  )
    ? dbUser.okToSendSectionReminders
    : true;
  computedUserData.sentFirstEmail = dbUser?.sentFirstEmail || false;
  computedUserData.aboutMe = dbUser?.aboutMe || '';
  computedUserData.goals = dbUser?.goals || '';
  computedUserData.wantToConnect = dbUser?.wantToConnect || true;
  computedUserData.preferredLanguage = dbUser?.preferredLanguage || '';
  // dbUserRest.displayName = computedUserData.displayName

  // The resulting computed object prioritizes values that have been manually
  // set on the Firestore user (which happens when the user updates their
  // profile) over any defaults or derived values. The one exception is roles,
  // which always uses the computed values.
  const userData: CourseUser = {
    id: authUser.uid,
    ...USER_DEFAULTS,
    ...dbUserRest,
    ...computedUserData,
  };

  return userData;
}

export function getCourseRoleNumber(courseRoleStr: RoleFirebaseString) {
  if (!courseRoleStr) return Role.UNREGISTERED;
  return firebaseStringToRole(courseRoleStr);
}


function calculateUserAge(userProtectedData) {
  if (!userProtectedData) return undefined;
  const dob = userProtectedData.dateOfBirth;
  if (!dob) return undefined;
  return getAgeFromDateOfBirth({dob});
}
